import React from 'react';
import { createRoot } from 'react-dom/client';

import * as serviceWorker from './serviceWorker';
import './index.scss';
import { BrowserRouter, Routes, Route, RouteComponentProps } from 'react-router-dom';
import AppContext, {IAppContext} from './context/AppContext';

import axios from 'axios';
import { Http } from './utils/http';


import './scss/qsa-public.scss';

import '../node_modules/quill/dist/quill.snow.css';

import HomePage from './recordViews/Home';
import AgencyPage from './recordViews/Agency';
import GenericErrorPage from './recordViews/GenericErrorPage';
import NotFound from './recordViews/NotFound';
import ResultsPage from './recordViews/Results';
import SeriesPage from './recordViews/Series';
import FunctionPage from './recordViews/Function';
import MandatePage from './recordViews/Mandate';
import ItemPage from './recordViews/Item';
import {EndImpersonationPage, LoginPage, LogoutPage} from './recordViews/UserSession';
import PageViewPage from './recordViews/PageViewPage';
import VerifyCaptchaForDownloadPage from './recordViews/VerifyCaptchaForDownload';

import AppContextProvider from './context/AppContextProvider';
import {
  BannedTagsManagementPage,
  ChangePasswordPage,
  MyAccountPage,
  MyContactDetailsPage,
  MyRequestsPage,
  RegisterPage, TagManagementPage,
  UserManagementPage,
  PageManagementPage, MySearchesPage, MyRequestPage,
} from './recordViews/Users';
import { PasswordRecoveryPage } from './recordViews/PasswordRecovery';

import { MyReadingRoomRequestsCartPage } from './cart/MyReadingRoomRequestsCartPage';
import { DigitalCopyCartSummaryPage } from './cart/DigitalCopyCartSummaryPage';
import { DigitalCopyRequestQuotePage } from './cart/DigitalCopyRequestQuotePage';
import { DigitalCopySetPricePage } from "./cart/DigitalCopySetPricePage";
import { DigitalCopyMinicartPage } from "./cart/DigitalCopyMinicartPage";

import ErrorPage from "./context/ErrorPage";

import {RecordDisplay} from "./models/RecordDisplay";
import SubjectPage from "./recordViews/Subject";

/* Establish error handling */
class ErrorBuffer {
  static FLUSH_DELAY_MS = 2000;

  private errors: ErrorEvent[];
  private consoleMessages: { method: string; arguments: any[] }[];
  private flushing: boolean;

  constructor() {
    this.errors = [];
    this.consoleMessages = [];
    this.flushing = false;

    this.startTimer();
  }

  private startTimer() {
    setInterval(() => {
      this.flush();
    }, ErrorBuffer.FLUSH_DELAY_MS);
  }

  private flush() {
    if (this.flushing || (this.errors.length === 0 && this.consoleMessages.length === 0)) {
      return;
    }

    const errors = this.errors;
    const consoleMessages = this.consoleMessages;

    this.errors = [];
    this.consoleMessages = [];

    this.flushing = true;
    axios
      .post(`${process.env.REACT_APP_QSA_PUBLIC_URL || ''}/api/error_report`, {
        errors: this.formatErrors(errors),
        consoleMessages: this.formatMessages(consoleMessages)
      })
      .finally(() => {
        this.flushing = false;
      });
  }

  private formatErrors(errors: ErrorEvent[]): any[] {
    return errors.map((error: ErrorEvent) => {
      return {
        message: error.message,
        filename: error.filename,
        lineno: error.lineno,
        colno: error.colno,
        stack: (error.error && error.error.stack) ? error.error.stack : [],
      };
    });
  }

  private formatMessages(messages: any[]): any[] {
    return messages;
  }

  addError(error: ErrorEvent) {
    this.errors.push(error);
  }

  addConsoleMessage(msg: any) {
    this.consoleMessages.push(msg);
  }
}

const appErrorBuffer = new ErrorBuffer();

/* Hook console to capture messages */
const browserConsole: any = console;
const appConsole: any = {};

Object.keys(browserConsole).forEach(function(key) {
  appConsole[key] = function(...rest: any[]) {
    appErrorBuffer.addConsoleMessage({
      method: key,
      arguments: rest
    });

    // eslint-disable-next-line prefer-rest-params
    browserConsole[key].apply(this, arguments);
  };
});

// eslint-disable-next-line no-global-assign
console = appConsole;

/* Grab errors that hit the top-level */
window.addEventListener('error', function(event) {
  appErrorBuffer.addError(event);

  return false;
});

let routeKey = 0;

function wrappedRoute(component: any, opts: { alwaysRender?: boolean; pageTitle?: string; deferTriggerPageView?: boolean } = {}): any {
  if (typeof opts.alwaysRender === 'undefined') {
    opts.alwaysRender = true;
  }

  // eslint-disable-next-line react/display-name
  return (props: RouteComponentProps<any>) => {
    if (opts.alwaysRender) {
      window.scrollTo(0, 0);
    }

    // Clear noindex that might have been added by the layout during a 404
    document.head.querySelectorAll('meta[name="robots"][content="noindex"]').forEach(meta => {
      meta && meta.parentNode && meta.parentNode.removeChild(meta);
    });

    const setPageTitle = (s: string) => {
      const title = document.head.querySelector('title');
      if (title) {
        title.innerText = s;
      }
    };

    if (opts.pageTitle) {
      setPageTitle(opts.pageTitle);
    }

    routeKey++;

    return (<ErrorPage>
      <AppContext.Consumer>
        {
          (context: IAppContext) => {
            Http.sessionNotificationHandler = (state) => {
              if (state.sessionGone) {
                context.setShowLoggedOutMessage(true);
              }

              if (state.sessionVersion) {
                context.setSessionVersion(state.sessionVersion);
              }

              context.setIsImpersonating(state.sessionIsImpersonating);
            }

            return React.createElement(
              component,
              Object.assign({},
                            props,
                            {
                              routeKey,
                              setPageTitle,
                              context
                            },
                            opts.alwaysRender ? { key: routeKey } : {}));
          }
        }
      </AppContext.Consumer>
    </ErrorPage>);
  };
}

const container = document.getElementById('root');
const root = createRoot(container);

root.render(
  <AppContextProvider>
    <BrowserRouter>
      <Routes>
        <Route
          exact
          path="/"
          Component={wrappedRoute(HomePage, {
            pageTitle: 'ArchivesSearch: Home'
          })}
        />
        <Route path="/agencies/:qsaId" Component={wrappedRoute(AgencyPage, { pageTitle: 'View agency', deferTriggerPageView: true })} />
        <Route path="/series/:qsaId" Component={wrappedRoute(SeriesPage, { pageTitle: 'View series', deferTriggerPageView: true })} />
        <Route path="/functions/:qsaId" Component={wrappedRoute(FunctionPage, { pageTitle: 'View function', deferTriggerPageView: true })} />
        <Route path="/mandates/:qsaId" Component={wrappedRoute(MandatePage, { pageTitle: 'View mandate', deferTriggerPageView: true })} />
        <Route path="/items/:qsaId" Component={wrappedRoute(ItemPage, { pageTitle: 'View item', deferTriggerPageView: true })} />
        <Route path="/subjects/:qsaId" Component={wrappedRoute(SubjectPage, { pageTitle: 'View subject', deferTriggerPageView: true })} />
        <Route exact path="/login" Component={wrappedRoute(LoginPage, { pageTitle: 'Login' })} />
        <Route exact path="/search" Component={wrappedRoute(ResultsPage, { pageTitle: 'Search records' })} />
        <Route exact path="/register" Component={wrappedRoute(RegisterPage, { pageTitle: 'Register' })} />
        <Route
          exact
          path="/recover-password/:token?"
          Component={wrappedRoute(PasswordRecoveryPage, { pageTitle: 'Password Recovery' })}
        />
        <Route exact path="/my-account" Component={wrappedRoute(MyAccountPage, { pageTitle: 'My Account' })} />
        <Route exact path="/reading-room-requests-cart" Component={wrappedRoute(MyReadingRoomRequestsCartPage, { pageTitle: 'Pending Reading Room Requests' })} />
        <Route exact path="/digital-copies-cart" Component={wrappedRoute(DigitalCopyCartSummaryPage, { pageTitle: 'Pending Digital Copy Requests' })} />
        <Route exact path="/digital-copies-cart/request-quote" Component={wrappedRoute(DigitalCopyRequestQuotePage, { pageTitle: 'Request Quote for Digital Copies' })} />
        <Route exact path="/digital-copies-cart/set-price-checkout" Component={wrappedRoute(DigitalCopySetPricePage, { pageTitle: 'Checkout Set Price Digital Copies' })} />
        <Route exact path="/digital-copies-cart/minicart" Component={wrappedRoute(DigitalCopyMinicartPage, { pageTitle: 'Confirm Your Order' })} />
        <Route exact path="/my-requests" Component={wrappedRoute(MyRequestsPage, { pageTitle: 'My Requests' })} />
        <Route exact path="/my-requests/:id" Component={wrappedRoute(MyRequestPage, { pageTitle: 'My Request' })} />
        <Route
          exact
          path="/my-details"
          Component={wrappedRoute(MyContactDetailsPage, {
            pageTitle: 'My Contact Details'
          })}
        />
        <Route
          exact
          path="/change-password"
          Component={wrappedRoute(ChangePasswordPage, {
            pageTitle: 'Change Password'
          })}
        />
        <Route
          exact
          path="/admin/users"
          Component={wrappedRoute(UserManagementPage, {
            pageTitle: 'User Management'
          })}
        />
        <Route
          exact
          path="/admin/users/:user_id"
          Component={wrappedRoute(UserManagementPage, {
            pageTitle: 'User Management'
          })}
        />
        <Route
          exact
          path="/logout"
          Component={wrappedRoute(LogoutPage, {
            pageTitle: 'Logged Out'
          })}
        />
        <Route
          exact
          path="/end-impersonation"
          Component={wrappedRoute(EndImpersonationPage, {
            pageTitle: 'Logged Out'
          })}
        />
        <Route
            exact
            path="/admin/tags"
            Component={wrappedRoute(TagManagementPage, {
              pageTitle: 'Tag Management'
            })}
        />
        <Route
            exact
            path="/admin/banned-tags"
            Component={wrappedRoute(BannedTagsManagementPage, {
              pageTitle: 'Banned Tag Management'
            })}
        />
        <Route
            exact
            path="/admin/pages"
            Component={wrappedRoute(PageManagementPage, {
              pageTitle: 'Page Management'
            })}
        />
        <Route
            exact
            path="/admin/pages/:slug"
            Component={wrappedRoute(PageManagementPage, {
              pageTitle: 'Edit Page'
            })}
        />
        <Route
            exact
            path="/my-searches"
            Component={wrappedRoute(MySearchesPage, {
              pageTitle: 'My Searches'
            })}
        />
        <Route
            exact
            path="/pages/:slug"
            Component={wrappedRoute(PageViewPage, {
              pageTitle: 'View Page',
              deferTriggerPageView: true,
            })}
        />
        <Route exact path="/verify-captcha-for-download/:qsaId"
            Component={wrappedRoute(VerifyCaptchaForDownloadPage, {
              pageTitle: 'Digital Copy - Download',
            })}
        />

        <Route
          exact
          path="/error"
          Component={wrappedRoute(GenericErrorPage, { pageTitle: 'System error' })}
        />

        <Route Component={wrappedRoute(NotFound, { pageTitle: 'Page not found' })} />
      </Routes>
    </BrowserRouter>
  </AppContextProvider>
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
